import React, { useState, useEffect, useCallback } from 'react';
import { Todo, TodosResponse } from '../../types/todo';
import { todoService } from '../../services/todoService';
import { useCategories } from '../../context/CategoriesContext';
import TodoCard from './TodoCard';
import EditTodoModal from './EditTodoModal';
import TodoDetailModal from './TodoDetailModal';
import ConfirmModal from '../ui/ConfirmModal';

const TodoList: React.FC = () => {
  const { categories } = useCategories();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed' | 'overdue'>('pending');
  const [category, setCategory] = useState<string>('');
  const [sortBy, setSortBy] = useState<'received' | 'due' | 'priority' | 'category'>('received');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [error, setError] = useState('');
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [viewingTodo, setViewingTodo] = useState<Todo | null>(null);
  const [deletingTodoId, setDeletingTodoId] = useState<string | null>(null);
  const [draggedTodo, setDraggedTodo] = useState<Todo | null>(null);
  const [draggedOverId, setDraggedOverId] = useState<string | null>(null);
  const [selectedTodos, setSelectedTodos] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  

  // Clear selections when filter changes
  useEffect(() => {
    setSelectedTodos(new Set());
    setShowBulkActions(false);
  }, [filter, category, sortBy]);

  useEffect(() => {
    const fetchTodos = async () => {
      try {
        setLoading(true);
        setError('');
        
        const params = {
          page,
          limit: itemsPerPage,
          ...(filter !== 'all' && filter !== 'overdue' && { completed: filter === 'completed' }),
          ...(category && { category }),
        };

        const response: TodosResponse = await todoService.getTodos(params);
        setTodos(response.todos);
        setTotalPages(response.pagination.pages);
      } catch (err) {
        setError('Failed to load todos');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTodos();
  }, [filter, category, page, itemsPerPage]);

  const handleToggle = async (id: string) => {
    try {
      console.log('Attempting to toggle todo:', id);
      const updatedTodo = await todoService.toggleTodo(id);
      console.log('Toggle successful, updated todo:', updatedTodo);
      setTodos(todos.map(todo => (todo._id === id ? updatedTodo : todo)));

      // Update viewingTodo if it's currently being viewed
      if (viewingTodo && viewingTodo._id === id) {
        setViewingTodo(updatedTodo);
      }
    } catch (err: any) {
      console.error('Failed to toggle todo:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url
      });
      alert(`Failed to toggle todo: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleDelete = useCallback((id: string) => {
    setDeletingTodoId(id);
  }, []);

  const confirmDelete = async () => {
    if (!deletingTodoId) return;
    try {
      await todoService.deleteTodo(deletingTodoId);
      setTodos(todos.filter(todo => todo._id !== deletingTodoId));
      setDeletingTodoId(null);
    } catch (err) {
      console.error('Failed to delete todo:', err);
      alert('Failed to delete todo');
    }
  };

  const handleEdit = useCallback((todo: Todo) => {
    setEditingTodo(todo);
  }, []);

  const handleViewDetails = useCallback((todo: Todo) => {
    setViewingTodo(todo);
  }, []);

  const handleSelectTodo = (todoId: string) => {
    const newSelected = new Set(selectedTodos);
    if (newSelected.has(todoId)) {
      newSelected.delete(todoId);
    } else {
      newSelected.add(todoId);
    }
    setSelectedTodos(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = () => {
    const filteredTodos = getFilteredAndSortedTodos();
    if (selectedTodos.size === filteredTodos.length) {
      setSelectedTodos(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedTodos(new Set(filteredTodos.map(t => t._id)));
      setShowBulkActions(true);
    }
  };

  const handleBulkCategoryUpdate = async (newCategory: string) => {
    if (selectedTodos.size === 0) return;

    try {
      const todoIds = Array.from(selectedTodos);
      const result = await todoService.bulkUpdateTodos(todoIds, { category: newCategory });

      // Update local state
      setTodos(todos.map(todo => {
        const updatedTodo = result.todos.find(t => t._id === todo._id);
        return updatedTodo || todo;
      }));

      // Clear selections
      setSelectedTodos(new Set());
      setShowBulkActions(false);
    } catch (err) {
      console.error('Failed to bulk update todos:', err);
      alert('Failed to update todos');
    }
  };

  const handleBulkPriorityUpdate = async (newPriority: 'low' | 'medium' | 'high' | 'urgent') => {
    if (selectedTodos.size === 0) return;

    try {
      const todoIds = Array.from(selectedTodos);
      const result = await todoService.bulkUpdateTodos(todoIds, { priority: newPriority });

      // Update local state
      setTodos(todos.map(todo => {
        const updatedTodo = result.todos.find(t => t._id === todo._id);
        return updatedTodo || todo;
      }));

      // Clear selections
      setSelectedTodos(new Set());
      setShowBulkActions(false);
    } catch (err) {
      console.error('Failed to bulk update todos:', err);
      alert('Failed to update todos');
    }
  };

  const getFilteredAndSortedTodos = () => {
    let filteredTodos = todos;
    
    if (filter === 'overdue') {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      filteredTodos = todos.filter(todo => {
        if (todo.completed || !todo.dates.due) return false;
        return new Date(todo.dates.due) < now;
      });
    }
    
    const sortedTodos = [...filteredTodos].sort((a, b) => {
      if (sortBy === 'due') {
        const aDue = a.dates.due ? new Date(a.dates.due).getTime() : Infinity;
        const bDue = b.dates.due ? new Date(b.dates.due).getTime() : Infinity;
        const now = Date.now();
        
        const aOverdue = aDue < now && !a.completed;
        const bOverdue = bDue < now && !b.completed;
        
        if (aOverdue && !bOverdue) return -1;
        if (!aOverdue && bOverdue) return 1;
        
        return aDue - bDue;
      } else if (sortBy === 'priority') {
        const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      } else if (sortBy === 'category') {
        // Sort by category alphabetically, with uncategorized items last
        const aCategory = a.category || 'zzz_uncategorized';
        const bCategory = b.category || 'zzz_uncategorized';
        return aCategory.localeCompare(bCategory);
      } else {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return new Date(b.dates.received).getTime() - new Date(a.dates.received).getTime();
      }
    });
    
    return sortedTodos;
  };

  const handleSaveEdit = async (id: string, updates: Partial<Todo>) => {
    try {
      const updatedTodo = await todoService.updateTodo(id, updates);
      setTodos(todos.map(t => t._id === id ? updatedTodo : t));
      setEditingTodo(null);
    } catch (err) {
      console.error('Failed to update todo:', err);
      alert('Failed to update todo');
    }
  };

  const handleDragStart = (e: React.DragEvent, todo: Todo) => {
    // Don't start drag if the target is an input, button, or other interactive element
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON' ||
      target.tagName === 'A' ||
      target.closest('button') ||
      target.closest('input') ||
      target.closest('a') ||
      target.classList.contains('cursor-pointer')
    ) {
      e.preventDefault();
      return;
    }

    setDraggedTodo(todo);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', todo._id);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    // Clean up
    setDraggedTodo(null);
    setDraggedOverId(null);
  };

  const handleDragOver = (e: React.DragEvent, todoId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedTodo || draggedTodo._id === todoId) return;
    
    setDraggedOverId(todoId);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if we're actually leaving the drop zone
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setDraggedOverId(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetTodo: Todo) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clean up drag state
    const tempDraggedTodo = draggedTodo;
    setDraggedTodo(null);
    setDraggedOverId(null);
    
    if (!tempDraggedTodo || tempDraggedTodo._id === targetTodo._id) {
      return;
    }

    // Calculate new order
    const oldIndex = todos.findIndex(t => t._id === tempDraggedTodo._id);
    const newIndex = todos.findIndex(t => t._id === targetTodo._id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Create new array with item moved
    const newTodos = [...todos];
    const [movedItem] = newTodos.splice(oldIndex, 1);
    newTodos.splice(newIndex, 0, movedItem);
    
    // Update UI immediately for responsiveness
    setTodos(newTodos);

    // Persist the new order to the database
    try {
      const todoIds = newTodos.map(todo => todo._id);
      const updatedTodos = await todoService.reorderTodos(todoIds);
      // Update with server response to ensure consistency
      setTodos(updatedTodos);
    } catch (error) {
      console.error('Failed to save todo order:', error);
      // Revert to original order on error
      const response = await todoService.getTodos({ 
        page, 
        limit: itemsPerPage,
        ...(filter !== 'all' && filter !== 'overdue' && { completed: filter === 'completed' }),
        ...(category && { category })
      });
      setTodos(response.todos);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'pending'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('overdue')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'overdue'
                ? 'bg-red-600 text-white'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            Overdue
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'completed'
                ? 'bg-green-600 text-white'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            Completed
          </button>
        </div>

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Categories</option>
          {categories.map((cat: any) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'received' | 'due' | 'priority' | 'category')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="received">Sort by: Received</option>
          <option value="due">Sort by: Due Date</option>
          <option value="priority">Sort by: Priority</option>
          <option value="category">Sort by: Category</option>
        </select>
      </div>

      {showBulkActions && (
        <div className="bg-blue-50 border-b border-blue-200 p-3 -mx-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-blue-900">
                {selectedTodos.size} item{selectedTodos.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => {
                  setSelectedTodos(new Set());
                  setShowBulkActions(false);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Set category:</label>
                <select
                  onChange={(e) => handleBulkCategoryUpdate(e.target.value)}
                  className="text-sm px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  defaultValue=""
                >
                  <option value="" disabled>Choose category</option>
                  {categories.map((cat: any) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Set priority:</label>
                <select
                  onChange={(e) => handleBulkPriorityUpdate(e.target.value as 'low' | 'medium' | 'high' | 'urgent')}
                  className="text-sm px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  defaultValue=""
                >
                  <option value="" disabled>Choose priority</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {getFilteredAndSortedTodos().length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No todos found</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 -mx-4 border-t border-b border-gray-200">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedTodos.size === getFilteredAndSortedTodos().length && getFilteredAndSortedTodos().length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Select all</span>
            </div>
          </div>
          <div className="-mx-4 divide-y divide-gray-200">
            {getFilteredAndSortedTodos().map((todo, index) => {
              const isDraggedOver = draggedOverId === todo._id;
              const isDragging = draggedTodo?._id === todo._id;
              const canDrag = sortBy === 'received' && filter !== 'overdue' && !category;
              
              return (
                <div
                  key={todo._id}
                  className="relative"
                >
                  {/* Drop indicator line */}
                  {isDraggedOver && (
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500 z-10" />
                  )}
                  
                  <div
                    draggable={canDrag}
                    onDragStart={(e) => canDrag && handleDragStart(e, todo)}
                    onDragEnd={(e) => canDrag && handleDragEnd(e)}
                    onDragOver={(e) => canDrag && handleDragOver(e, todo._id)}
                    onDragLeave={(e) => canDrag && handleDragLeave(e)}
                    onDrop={(e) => canDrag && handleDrop(e, todo)}
                    className={`
                      relative flex items-start
                      ${canDrag ? 'hover:shadow-sm' : ''}
                      ${isDragging ? 'opacity-30 scale-95' : ''}
                      ${isDraggedOver && !isDragging ? 'mt-1' : ''}
                      ${selectedTodos.has(todo._id) ? 'bg-blue-50' : ''}
                      transition-all duration-150 ease-in-out
                    `}
                    style={{
                      cursor: canDrag ? 'grab' : 'default',
                      ...(isDragging && { cursor: 'grabbing' })
                    }}
                  >
                    <div className="pl-4 pr-2 py-2 flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedTodos.has(todo._id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectTodo(todo._id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        title="Select for bulk actions"
                      />
                    </div>
                    {canDrag && (
                      <div className="absolute left-12 top-1/2 -translate-y-1/2 text-gray-400 opacity-0 hover:opacity-100 transition-opacity">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1">
                      <TodoCard
                        todo={todo}
                        onToggle={handleToggle}
                        onDelete={handleDelete}
                        onEdit={handleEdit}
                        onViewDetails={handleViewDetails}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-6">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <div className="ml-4 border-l pl-4">
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setPage(1); // Reset to first page when changing items per page
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                  <option value={250}>250 per page</option>
                </select>
              </div>
            </div>
          )}

          {totalPages <= 1 && (
            <div className="flex justify-center items-center mt-6">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Show:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setPage(1); // Reset to first page when changing items per page
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={25}>25 items</option>
                  <option value={50}>50 items</option>
                  <option value={100}>100 items</option>
                  <option value={250}>250 items</option>
                </select>
              </div>
            </div>
          )}
        </>
      )}

      {sortBy !== 'received' || filter === 'overdue' || category ? (
        <div className="flex items-center justify-center gap-1 text-xs text-amber-600 mt-3 mb-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Reordering disabled while filtering or sorting</span>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-1 text-xs text-gray-400 mt-3 mb-2">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
          <span>Drag items to reorder</span>
        </div>
      )}
      
      <EditTodoModal
        isOpen={!!editingTodo}
        onClose={() => setEditingTodo(null)}
        todo={editingTodo}
        onSave={handleSaveEdit}
      />

      <TodoDetailModal
        isOpen={!!viewingTodo}
        onClose={() => setViewingTodo(null)}
        todo={viewingTodo}
        onToggle={handleToggle}
      />

      <ConfirmModal
        isOpen={!!deletingTodoId}
        onClose={() => setDeletingTodoId(null)}
        onConfirm={confirmDelete}
        title="Delete Todo"
        message="Are you sure you want to delete this todo? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default TodoList;